import { WORKOUT_CONSTANTS } from '../../config/constants';
import { safeValidateSessionState } from '../../validation/workout.schemas';
import { dayjs } from '../date';

import type {
  SessionState,
  SessionDuration,
  SessionProgress,
  SerializedSession,
  WorkoutExercise,
} from '../../types';
import type { WorkoutSessionInput } from '../../validation/workout.schemas';

/**
 * Session Utility Functions
 *
 * Framework-agnostic utility functions for workout session management,
 * duration calculation, validation, and progress tracking.
 */

const { SESSION_SERIALIZATION_VERSION } = WORKOUT_CONSTANTS;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Calculate session duration between start and end times
 */
export function calculateSessionDuration(
  startedAt: number,
  completedAt?: number | null
): SessionDuration {
  const endTime = completedAt || dayjs().valueOf();
  const totalMilliseconds = Math.max(0, endTime - startedAt);

  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const seconds = totalSeconds % 60;

  // Format as HH:MM:SS or MM:SS depending on duration
  const formatted =
    hours > 0
      ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return {
    total_milliseconds: totalMilliseconds,
    total_seconds: totalSeconds,
    total_minutes: totalMinutes,
    hours,
    minutes,
    seconds,
    formatted,
  };
}

/**
 * Serialize session data for storage (framework-agnostic)
 * Returns a serialized object that can be converted to JSON
 */
export function serializeSessionForStorage(
  session: SessionState
): SerializedSession {
  try {
    const serializedData = JSON.stringify(session);

    return {
      version: SESSION_SERIALIZATION_VERSION,
      timestamp: dayjs().valueOf(),
      data: serializedData,
    };
  } catch (error) {
    throw new Error(
      `Failed to serialize session data: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Deserialize session data from storage (framework-agnostic)
 * Accepts serialized session object and returns SessionState
 */
export function deserializeSessionFromStorage(
  serializedSession: SerializedSession | string
): SessionState {
  try {
    let parsedData: SerializedSession;

    // Handle both string and object inputs for flexibility
    if (typeof serializedSession === 'string') {
      parsedData = JSON.parse(serializedSession) as SerializedSession;
    } else {
      parsedData = serializedSession;
    }

    // Validate serialized data structure
    if (!parsedData.data || !parsedData.version || !parsedData.timestamp) {
      throw new Error('Invalid serialized session format');
    }

    // For now, we support version 1.0.0, but this allows for future versioning
    if (parsedData.version !== SESSION_SERIALIZATION_VERSION) {
      console.warn(
        `Session data version mismatch. Expected ${SESSION_SERIALIZATION_VERSION}, got ${parsedData.version}`
      );
    }

    const sessionData = JSON.parse(parsedData.data) as SessionState;

    // Validate deserialized data using schema validation
    const parseResult = safeValidateSessionState(sessionData);
    if (!parseResult.success) {
      const errors = parseResult.error.issues.map((e) => e.message);
      throw new Error(`Invalid session data: ${errors.join(', ')}`);
    }

    return sessionData;
  } catch (error) {
    throw new Error(
      `Failed to deserialize session data: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Calculate session progress based on completed exercises
 */
export function calculateSessionProgress(
  session: SessionState
): SessionProgress {
  const totalExercises = session.workout.exercises.length;
  const completedExercises = session.completed_exercises?.length || 0;

  const percentage =
    totalExercises > 0
      ? Math.round((completedExercises / totalExercises) * 100)
      : 0;

  const remainingExercises = Math.max(0, totalExercises - completedExercises);
  const isComplete = completedExercises >= totalExercises && totalExercises > 0;

  return {
    total_exercises: totalExercises,
    completed_exercises: completedExercises,
    percentage,
    remaining_exercises: remainingExercises,
    is_complete: isComplete,
  };
}

/**
 * Merge session progress updates with current session state
 */
export function mergeSessionProgress(
  current: SessionState,
  updates: Partial<SessionState>
): SessionState {
  // Create a deep copy of the current session to avoid mutations
  const merged: SessionState = {
    start: current.start,
    active: current.active,
    state: current.state,
    workout: {
      workout_id: current.workout.workout_id,
      start_time: current.workout.start_time,
      exercises: [...current.workout.exercises],
      guests: current.workout.guests ? [...current.workout.guests] : null,
    },
    completed_exercises: current.completed_exercises
      ? [...current.completed_exercises]
      : null,
    duration: current.duration,
    abandoned_at: current.abandoned_at,
  };

  // Apply updates
  if (updates.active !== undefined) {
    merged.active = updates.active;
  }

  if (updates.state !== undefined) {
    merged.state = updates.state;
  }

  if (updates.completed_exercises !== undefined) {
    merged.completed_exercises = updates.completed_exercises
      ? [...updates.completed_exercises]
      : null;
  }

  if (updates.duration !== undefined) {
    merged.duration = updates.duration;
  }

  if (updates.abandoned_at !== undefined) {
    merged.abandoned_at = updates.abandoned_at;
  }

  // Update workout data if provided
  if (updates.workout) {
    if (updates.workout.exercises) {
      merged.workout.exercises = [...updates.workout.exercises];
    }

    if (updates.workout.guests !== undefined) {
      merged.workout.guests = updates.workout.guests
        ? [...updates.workout.guests]
        : null;
    }

    if (updates.workout.workout_id) {
      merged.workout.workout_id = updates.workout.workout_id;
    }

    if (updates.workout.start_time) {
      merged.workout.start_time = updates.workout.start_time;
    }
  }

  return merged;
}

/**
 * Helper function to check if a session is active
 */
export function isSessionActive(session: SessionState): boolean {
  return session.active && !session.abandoned_at && session.duration === null;
}

/**
 * Helper function to check if a session is completed
 */
export function isSessionCompleted(session: SessionState): boolean {
  const progress = calculateSessionProgress(session);
  return progress.is_complete || session.duration !== null;
}

/**
 * Helper function to get session elapsed time
 */
export function getSessionElapsedTime(session: SessionState): number {
  if (session.duration !== null && session.duration !== undefined) {
    return session.duration;
  }

  if (session.abandoned_at) {
    return session.abandoned_at - session.start;
  }

  return dayjs().valueOf() - session.start;
}

// =============================================================================
// TRANSFORMATION FUNCTIONS
// =============================================================================

/**
 * Transform a set from SessionState format to WorkoutSessionInput format.
 *
 * Filters out null/undefined values and deliberately DROPS `id`: the API
 * spreads each set object straight into the completed-set insert, so only
 * metric keys may travel.
 */
function transformSet(
  set: {
    id?: string | number;
    reps?: number | null;
    weight?: number | null;
    distance?: number | null;
    duration?: number | null;
  } | null
): {
  reps?: number;
  weight?: number;
  distance?: number;
  duration?: number;
} {
  if (!set) return {};

  const result: {
    reps?: number;
    weight?: number;
    distance?: number;
    duration?: number;
  } = {};

  if (set.reps !== undefined && set.reps !== null) {
    result.reps = set.reps;
  }
  if (set.weight !== undefined && set.weight !== null) {
    result.weight = set.weight;
  }
  if (set.distance !== undefined && set.distance !== null) {
    result.distance = set.distance;
  }
  if (set.duration !== undefined && set.duration !== null) {
    result.duration = set.duration;
  }

  return result;
}

/**
 * Transform a member exercise from WorkoutExercise format to exerciseMember format
 */
function transformMember(member: WorkoutExercise): {
  name: string;
  guid: string;
  tracking_type:
    | 'reps_only'
    | 'weight_reps'
    | 'time_based'
    | 'distance_based'
    | 'time_distance'
    | 'weight_time';
  sets: Array<{
    reps?: number;
    weight?: number;
    distance?: number;
    duration?: number;
  }>;
} {
  return {
    name: member.name,
    guid: String(member.guid),
    tracking_type: member.tracking_type,
    sets: (member.sets ?? []).map(transformSet),
  };
}

/**
 * Transform an exercise from SessionState format to WorkoutSessionInput format.
 *
 * Every entry — grouped or not — is a group leader carrying its own sets;
 * `members` holds the rest of the group. The wire key is `group_type`
 * (the API's `array:` allowlist rejects `type`).
 */
function transformExercise(
  exercise: WorkoutExercise
): WorkoutSessionInput['exercises'][number] {
  const entry = {
    name: exercise.name,
    guid: String(exercise.guid),
    wildcard: exercise.wildcard,
    group_type: exercise.group_type ?? 'regular',
    tracking_type: exercise.tracking_type,
    sets: (exercise.sets ?? []).map(transformSet),
  };

  if (exercise.members && exercise.members.length > 0) {
    return {
      ...entry,
      members: exercise.members.map(transformMember),
    } as WorkoutSessionInput['exercises'][number];
  }

  return entry as WorkoutSessionInput['exercises'][number];
}

/**
 * Options for building the stop payload beyond the exercise log itself.
 */
export interface TransformSessionOptions {
  /** Intentional light day — sent as `is_deload` */
  isDeload?: boolean;
  /** Accumulated paused time in whole seconds — the API subtracts it */
  pausedSeconds?: number;
}

/**
 * Transform SessionState to WorkoutSessionInput
 *
 * Converts the internal session state format to the schema-validated
 * format expected by the workout session API.
 *
 * This function is pure and does not mutate the original SessionState object.
 *
 * @param session - The current session state
 * @param effortRating - The user's effort rating (1-10, required by the API)
 * @param options - Deload flag and paused-seconds accounting
 * @returns WorkoutSessionInput ready for validation and submission
 *
 * @example
 * const input = transformSessionStateToWorkoutInput(session, 7, { pausedSeconds: 120 });
 * const result = safeValidateWorkoutSession(input);
 */
export function transformSessionStateToWorkoutInput(
  session: SessionState,
  effortRating: number,
  options: TransformSessionOptions = {}
): WorkoutSessionInput {
  // Transform exercises
  const exercises = session.workout.exercises.map(transformExercise);

  // Key guests by username; this format keeps no per-guest sets, so each
  // guest is sent with no exercises
  const guests = session.workout.guests
    ? Object.fromEntries(
        session.workout.guests.map((guest) => [
          guest.username,
          { exercises: [] },
        ])
      )
    : undefined;

  return {
    effort_rating: effortRating,
    exercises,
    guests,
    ...(options.isDeload !== undefined && { is_deload: options.isDeload }),
    ...(options.pausedSeconds !== undefined && {
      paused_seconds: options.pausedSeconds,
    }),
  };
}
